/**
 * palette.js — Single source of truth for all colors.
 *
 * To change a color globally, update the hex values here.
 * colors.js maps these raw scales to semantic roles (primary, error, etc.)
 * and all MUI component overrides consume colors.js — nothing else is hardcoded.
 *
 * Scale convention: 50 = lightest tint, 900 = darkest shade, 500 = base color.
 */

const palette = {
  // ─── Brand (cyan) ─────────────────────────────────────────────────────────
  // Base: #10b0d5  — used as primary / info
  brand: {
    50:  "#e8f8fd",
    100: "#bdedf8",
    200: "#8fe0f2",
    300: "#5fd3ec",
    400: "#30c5e5",
    500: "#10b0d5",   // ← change this one hex to shift the entire brand color
    600: "#0d9dbc",
    700: "#0a87a0",
    800: "#077082",
    900: "#044d5a",
  },

  // ─── Navy (dark UI chrome) ──────────────────────────────────────────────
  // Base: #344767  — used for sidebar, dark text, headings
  navy: {
    50:  "#edf0f5",
    100: "#cdd5e0",
    200: "#aabbcc",
    300: "#87a0b8",
    400: "#6d8ca8",
    500: "#547898",
    600: "#476a87",
    700: "#3a5872",
    800: "#344767",   // ← sidebar / heading base
    900: "#1e2d42",
  },

  // ─── Neutral (greys) ────────────────────────────────────────────────────
  // Covers background, borders, secondary text
  neutral: {
    50:  "#f8f9fa",
    100: "#f0f2f5",   // ← page background
    200: "#dee2e6",
    300: "#ced4da",   // ← input borders
    400: "#adb5bd",
    500: "#7b809a",   // ← secondary / muted text
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },

  // ─── Green (semantic success) ────────────────────────────────────────────
  // Base: #4CAF50
  green: {
    50:  "#e8f5e9",
    100: "#c8e6c9",
    200: "#a5d6a7",
    300: "#81c784",
    400: "#66bb6a",
    500: "#4caf50",   // ← success base
    600: "#43a047",
    700: "#388e3c",
    800: "#2e7d32",
    900: "#1b5e20",
  },

  // ─── Orange (accent / highlight) ─────────────────────────────────────────
  // Base: #f48326  — used as primary accent (watchlist, highlights)
  // orange[50] and orange[400] follow MUI Material Design orange for chip/badge bg
  orange: {
    50:  "#fff3e0",   // MUI orange[50] — chip/badge tint backgrounds
    100: "#ffddb3",
    200: "#ffc680",
    300: "#ffaf4d",
    400: "#ff9800",   // MUI orange[500] — in-progress status chips and indicators
    500: "#f48326",   // ← orange accent base (also covers #f6821e)
    600: "#e07420",
    700: "#c8631a",
    800: "#b05214",
    900: "#8c3d0b",
  },

  // ─── Amber (warning) ─────────────────────────────────────────────────────
  // Base: #fb8c00
  amber: {
    50:  "#fff8e1",
    100: "#ffecb3",
    200: "#ffe082",
    300: "#ffd54f",
    400: "#ffca28",
    500: "#ffc107",
    600: "#fb8c00",   // ← warning base
    700: "#f57c00",
    800: "#ef6c00",
    900: "#e65100",
  },

  // ─── Red (error / danger) ────────────────────────────────────────────────
  // Base: #F44336
  red: {
    50:  "#ffebee",
    100: "#ffcdd2",
    200: "#ef9a9a",
    300: "#e57373",
    400: "#ef5350",
    500: "#f44336",   // ← error base
    600: "#e53935",
    700: "#d32f2f",
    800: "#c62828",
    900: "#b71c1c",
  },

  // ─── Pink (alert banners, progress indicators) ───────────────────────────
  // Base: #ED5F72  — used for error/alert highlights in pages
  pink: {
    50:  "#fce4ec",
    100: "#f8bbd0",
    200: "#f48fb1",
    300: "#f06292",
    400: "#ED5F72",   // ← alert/highlight base
    500: "#e91e63",
    600: "#d81b60",
    700: "#c2185b",
    800: "#ad1457",
    900: "#880e4f",
  },

  // ─── Dark theme backgrounds ──────────────────────────────────────────────
  dark: {
    50:  "#323a54",
    100: "#202940",
    200: "#1f283e",
    300: "#1a2035",   // ← dark theme page background
    900: "#191919",
  },

  // ─── Gold (achievements, awards) ─────────────────────────────────────────
  gold: "#FFD700",

  // ─── Chart colors (data visualisation) ───────────────────────────────────
  // Used in utils.js COLORS array and Insight.js chart configs
  chart: {
    cyan:    "#4dc9f6",
    orange:  "#f67019",
    pink:    "#f53794",
    blue:    "#537bc4",
    lime:    "#acc236",
    teal:    "#166a8f",
    green:   "#00a950",
    darkGrey:"#58595b",
    violet:  "#8549ba",
    // Insight.js chart series
    grey:    "#747577",
    coral:   "#E65472",
    amber:   "#ED9243",
    yellow:  "#F4C658",
    seafoam: "#5DB2B2",
    sky:     "#478DDF",
    black:   "#111111",
    // Chart grid/axes
    gridLine: "#9ca2b7",
    border:   "#b2b9bf",
    surface:  "#f8f9fa",
  },

  // ─── MUI blue (info/link color, kept for backward compat) ────────────────
  blue: {
    50:  "#e3f2fd",
    100: "#bbdefb",
    200: "#90caf9",
    300: "#64b5f6",
    400: "#42a5f5",
    500: "#2196f3",
    600: "#1e88e5",
    700: "#1976d2",   // ← MUI default info blue
    800: "#1565c0",
    900: "#0d47a1",
  },

  // ─── Purple (status chips) ────────────────────────────────────────────────
  // purple[50] follows MUI purple[50] for chip/badge tint backgrounds
  purple: {
    50:  "#ede7f6",   // MUI purple[50] — chip/badge tint backgrounds
    100: "#e1bee7",
    200: "#ce93d8",
    300: "#ba68c8",
    400: "#ab47bc",
    500: "#9c27b0",
    600: "#8e24aa",
    700: "#7b1fa2",
    800: "#6a1b9a",
    900: "#4a148c",
  },

  // ─── Absolute values ─────────────────────────────────────────────────────
  white:       "#ffffff",
  black:       "#000000",
  transparent: "transparent",
};

export default palette;
